import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts'
import { ClipboardList, CheckCircle2, ChevronLeft, ChevronRight, TrendingDown, TrendingUp, Minus, AlertTriangle, Shield, Loader } from 'lucide-react'
import { useAssessmentHistory } from '../hooks/useSupabase'
import { callScales } from '../components/scales/RatingScalesModule'
import { useAuth } from '../lib/auth.jsx'
import styles from './AssessmentsPage.module.css'

// 2026-08-05 fix: was a hardcoded placeholder org id ('00000000-...-0001',
// the same dev/fallback UUID used elsewhere in this codebase as a
// disconnected-state stub) used for real scale-catalog lookups AND real
// assessment-result writes below — every provider's PHQ-9/GAD-7 data was
// being saved under this fake org regardless of their actual org. Replaced
// with the real session orgId from useAuth() inside the component.
const SCALE_CODES = ['PHQ-9', 'GAD-7']

const SEVERITY_PALETTE = [
  { color: '#2D8A4E', bg: 'rgba(45,138,78,0.08)' },
  { color: '#C97C2A', bg: 'rgba(201,124,42,0.08)' },
  { color: '#C0392B', bg: 'rgba(192,57,43,0.08)' },
  { color: '#8B1A1A', bg: 'rgba(139,26,26,0.08)' },
  { color: '#6C1D5F', bg: 'rgba(108,29,95,0.08)' },
]

const CHART_COLORS = { 'PHQ-9': '#2471A3', 'GAD-7': '#7D3C98' }

function buildScaleFromCatalog(meta, items, ranges) {
  const sortedRanges = [...(ranges || [])].sort((a, b) => a.max_score - b.max_score)
  const severity = sortedRanges.map((r, i) => ({
    max: r.max_score,
    label: r.range_label,
    ...(SEVERITY_PALETTE[Math.min(i, SEVERITY_PALETTE.length - 1)]),
  }))
  const sortedItems = [...(items || [])].sort((a, b) => a.item_number - b.item_number)

  let options = ['Not at all (0)', 'Several days (1)', 'More than half the days (2)', 'Nearly every day (3)']
  if (sortedItems[0]?.anchor_labels) {
    const labels = sortedItems[0].anchor_labels
    options = Object.entries(labels)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([val, text]) => `${text} (${val})`)
  }

  const safetyIdx = sortedItems.findIndex(item =>
    /self.harm|better off dead|hurting yourself|suicid/i.test(item.item_text)
  )

  const cutoffs = sortedRanges
    .filter((r, i) => i > 0 && r.min_score >= (meta.max_score || 0) * 0.3)
    .slice(0, 2)
    .map(r => ({ y: r.min_score, label: r.range_label }))

  return {
    name: meta.scale_full_name || meta.scale_name || meta.scale_code,
    abbr: meta.scale_code,
    max: meta.max_score || 0,
    items: sortedItems.map(i => i.item_text),
    options,
    severity: severity.length ? severity : [{ max: meta.max_score || 0, label: 'Unscored', color: '#999', bg: 'rgba(153,153,153,0.08)' }],
    safetyItem: safetyIdx >= 0 ? safetyIdx : undefined,
    cutoffs,
    chartColor: CHART_COLORS[meta.scale_code] || '#2471A3',
    scaleId: meta.scale_id,
  }
}

const getSeverity = (scale, score) =>
  scale.severity.find(s => score <= s.max) || scale.severity[scale.severity.length - 1]

// ── Trend indicator ──────────────────────────────────────────────────────────
function TrendBadge({ current, previous }) {
  if (previous == null) return null
  const delta = current - previous
  if (Math.abs(delta) < 2) return <span className={styles.trendFlat}><Minus size={12}/> Stable</span>
  if (delta < 0) return <span className={styles.trendGood}><TrendingDown size={12}/> {Math.abs(delta)} pts better</span>
  return <span className={styles.trendBad}><TrendingUp size={12}/> {delta} pts higher</span>
}

// ── Score history chart ──────────────────────────────────────────────────────
function ScoreChart({ history, scale }) {
  const chartData = history.map((h, i) => ({
    date: h.administered_at ? new Date(h.administered_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : `Administration ${i+1}`,
    score: h.total_score,
  }))

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null
    const score = payload[0].value
    const sev = getSeverity(scale, score)
    return (
      <div className={styles.tooltip}>
        <p className={styles.tooltipDate}>{label}</p>
        <p className={styles.tooltipScore} style={{ color: sev.color }}>{score} — {sev.label}</p>
      </div>
    )
  }

  if (!chartData.length) return <div className={styles.chartDemo}>No verified administrations are available for this patient and instrument.</div>

  return (
    <div className={styles.chartWrap}>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-100)" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--color-gray-400)' }} axisLine={false} tickLine={false} />
          <YAxis domain={[0, scale.max]} tick={{ fontSize: 11, fill: 'var(--color-gray-400)' }} axisLine={false} tickLine={false} />
          {scale.cutoffs?.map(c => (
            <ReferenceLine key={c.y} y={c.y} stroke="rgba(192,57,43,0.3)" strokeDasharray="4 4"
              label={{ value: c.label, fontSize: 10, fill: 'rgba(192,57,43,0.6)', position: 'right' }} />
          ))}
          <Tooltip content={<CustomTooltip />} />
          <Line type="monotone" dataKey="score" stroke={scale.chartColor} strokeWidth={2.5}
            dot={{ fill: scale.chartColor, r: 4, strokeWidth: 0 }}
            activeDot={{ r: 6, strokeWidth: 2, stroke: 'white' }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

// ── Assessment form ──────────────────────────────────────────────────────────
function AssessmentForm({ scale, onComplete, onCancel }) {
  const [answers, setAnswers] = useState(Array(scale.items.length).fill(null))
  const [step, setStep] = useState(0)
  const [submitted, setSubmitted] = useState(false)

  const answer = (val) => {
    const next = [...answers]; next[step] = val; setAnswers(next)
    if (step < scale.items.length - 1) setTimeout(() => setStep(s => s + 1), 280)
  }

  const total = answers.reduce((s, a) => s + (a ?? 0), 0)
  const allAnswered = answers.every(a => a !== null)
  const sev = getSeverity(scale, total)
  const safetyTriggered = scale.safetyItem != null && (answers[scale.safetyItem] ?? 0) > 0
  const progress = answers.filter(a => a !== null).length / scale.items.length

  const handleSubmit = () => {
    setSubmitted(true)
    onComplete({
      total,
      severity: sev.label,
      answers: answers.map((v, i) => ({ item: i + 1, score: v })),
      safetyTriggered,
      completedAt: new Date().toISOString(),
    })
  }

  if (submitted) return null

  return (
    <div className={styles.formWrap}>
      {/* Progress bar */}
      <div className={styles.formProgress}>
        <div className={styles.formProgressFill} style={{ width: `${progress * 100}%` }} />
      </div>

      <div className={styles.formHeader}>
        <span className={styles.formScale}>{scale.name}</span>
        <span className={styles.formCount}>{step + 1} of {scale.items.length}</span>
      </div>

      <p className={styles.formInstruction}>
        Over the <strong>past 2 weeks</strong>, how often have you been bothered by:
      </p>

      <div className={styles.formQuestion}>
        {scale.safetyItem != null && step === scale.safetyItem && (
          <div className={styles.safetyWarning}><AlertTriangle size={14}/> Safety screening item</div>
        )}
        <p className={styles.questionText}>"{scale.items[step]}"</p>
      </div>

      <div className={styles.answerGrid}>
        {scale.options.map((opt, idx) => (
          <button key={idx}
            className={`${styles.answerBtn} ${answers[step] === idx ? styles.answerBtnSel : ''}`}
            onClick={() => answer(idx)}>
            <span className={styles.answerScore}>{idx}</span>
            <span className={styles.answerLabel}>{opt.split(' (')[0]}</span>
          </button>
        ))}
      </div>

      <div className={styles.formNav}>
        <button className={styles.navBack} onClick={() => setStep(s => Math.max(0, s-1))} disabled={step === 0}>
          <ChevronLeft size={16}/> Back
        </button>
        {step < scale.items.length - 1 ? (
          <button className={styles.navNext} onClick={() => setStep(s => s+1)} disabled={answers[step] === null}>
            Next <ChevronRight size={16}/>
          </button>
        ) : (
          <button className={styles.navSubmit} onClick={handleSubmit} disabled={!allAnswered}>
            <CheckCircle2 size={16}/> Submit {scale.abbr}
          </button>
        )}
      </div>

      <button className={styles.cancelBtn} onClick={onCancel}>Cancel Assessment</button>
    </div>
  )
}

// ── Result card ──────────────────────────────────────────────────────────────
function ResultCard({ result, scale, onRetake, onClose }) {
  const sev = getSeverity(scale, result.total)
  return (
    <div className={styles.resultCard}>
      <CheckCircle2 size={40} style={{ color: sev.color }} />
      <h3 className={styles.resultTitle}>{scale.abbr} Complete</h3>
      <div className={styles.resultScore} style={{ color: sev.color, background: sev.bg }}>
        {result.total} / {scale.max}
      </div>
      <p className={styles.resultSeverity} style={{ color: sev.color }}>{sev.label}</p>
      {result.safetyTriggered && (
        <div className={styles.safetyAlert}>
          <AlertTriangle size={16}/>
          <div>
            <strong>Safety item endorsed</strong>
            <p>Patient endorsed thoughts of self-harm. Complete a safety assessment immediately.</p>
          </div>
        </div>
      )}
      <div className={styles.resultActions}>
        <button className={styles.retakeBtn} onClick={onRetake}>Retake</button>
        <button className={styles.doneBtn} onClick={onClose}>Done</button>
      </div>
    </div>
  )
}

export default function AssessmentsPage() {
  const { orgId, user, loading: authLoading } = useAuth()
  const [searchParams] = useSearchParams()
  const [scales, setScales]         = useState({})
  const [scaleOrder, setScaleOrder] = useState([])
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [activeTab, setActiveTab]   = useState(null)
  const [mode, setMode]             = useState('view')
  const [lastResult, setLastResult] = useState(null)
  const [saveError, setSaveError] = useState(null)
  const patientId = searchParams.get('patientId')
  const encounterId = searchParams.get('encounterId')
  const providerId = searchParams.get('providerId') || user?.user_metadata?.provider_id || ''
  const contextReady = Boolean(patientId && encounterId && providerId && orgId)

  useEffect(() => {
    if (authLoading) return
    if (!orgId) { setCatalogLoading(false); return }
    let cancelled = false
    async function loadCatalog() {
      try {
        const catalog = await callScales({ action: 'get_catalog', org_id: orgId })
        const metas = (catalog.scales || []).filter(s => SCALE_CODES.includes(s.scale_code))

        const details = await Promise.all(
          metas.map(m => callScales({ action: 'get_scale', org_id: orgId, scale_code: m.scale_code }))
        )

        if (cancelled) return

        const built = {}
        const order = []
        metas.forEach((m, i) => {
          const key = m.scale_code
          const d = details[i]
          built[key] = buildScaleFromCatalog(m, d?.items || [], d?.ranges || [])
          order.push(key)
        })

        setScales(built)
        setScaleOrder(order)
        if (order.length > 0) setActiveTab(prev => prev || order[0])
      } catch (err) {
        console.error('Failed to load scale catalog:', err)
      } finally {
        if (!cancelled) setCatalogLoading(false)
      }
    }
    loadCatalog()
    return () => { cancelled = true }
  }, [orgId, authLoading])

  const phqHistory = useAssessmentHistory(patientId, 'PHQ-9')
  const gadHistory = useAssessmentHistory(patientId, 'GAD-7')

  const currentHistory = activeTab === 'PHQ-9' ? phqHistory : gadHistory
  const history        = currentHistory.history || []
  const scale          = scales[activeTab]

  const latestScore    = history[history.length - 1]?.total_score
  const previousScore  = history[history.length - 2]?.total_score
  const latestSev      = scale && latestScore != null ? getSeverity(scale, latestScore) : { color: '#64748b', bg: '#f8fafc', label: 'No verified score' }
  const latestDate     = history[history.length - 1]?.administered_at

  const handleComplete = async (result) => {
    setSaveError(null)
    if (!contextReady) {
      setSaveError('Select a verified patient encounter before administering an assessment.')
      setMode('view')
      return
    }
    try {
      const saved = await callScales({
        action: 'score', org_id: orgId, patient_id: patientId, encounter_id: encounterId,
        provider_id: providerId, scale_code: activeTab,
        responses: Object.fromEntries(result.answers.map((answer) => [String(answer.item), answer.score])),
        administered_by: 'provider', administration_mode: 'in_session',
      })
      setLastResult({
        ...result,
        total: saved.total_score,
        severity: saved.severity?.severity_label || result.severity,
        safetyTriggered: saved.alert_triggered,
      })
      setMode('result')
    } catch (err) {
      setSaveError(err.message || 'The assessment could not be scored and saved.')
      setMode('view')
    }
  }

  if (catalogLoading) return (
    <div className={styles.page}>
      <div style={{ textAlign: 'center', padding: '4rem 0', opacity: 0.5 }}>
        <Loader size={28} className="animate-spin" style={{ margin: '0 auto 1rem' }} />
        <p>Loading assessment scales from catalog...</p>
      </div>
    </div>
  )

  if (!scale) return (
    <div className={styles.page}>
      <div style={{ textAlign: 'center', padding: '4rem 0', opacity: 0.5 }}>
        <p>No scales available. Check the catalog configuration.</p>
      </div>
    </div>
  )

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <ClipboardList size={28} style={{ color: 'var(--color-teal)' }}/>
          <div>
            <h1 className={styles.title}>Assessments</h1>
            <p className={styles.subtitle}>Validated mental health screening tools with trend analysis</p>
          </div>
        </div>
        {mode === 'view' && (
          <button className={styles.startBtn} onClick={() => setMode('taking')} disabled={!contextReady}>
            <ClipboardList size={16}/> Begin {scale.abbr}
          </button>
        )}
      </div>

      {!contextReady && <div className={styles.contextNotice}><AlertTriangle size={16}/><span>Select a patient encounter before administration. Trends and writes remain disabled without patient, encounter, provider, and organization context.</span></div>}
      {saveError && <div className={styles.contextNotice}><AlertTriangle size={16}/><span>{saveError}</span></div>}

      {/* Tabs */}
      <div className={styles.tabs}>
        {scaleOrder.map(key => (
          <button key={key} className={`${styles.tab} ${activeTab === key ? styles.tabActive : ''}`}
            onClick={() => { setActiveTab(key); setMode('view') }}>
            {scales[key].name}
          </button>
        ))}
      </div>

      {mode === 'taking' ? (
        <AssessmentForm scale={scale} onComplete={handleComplete} onCancel={() => setMode('view')} />
      ) : mode === 'result' ? (
        <ResultCard result={lastResult} scale={scale}
          onRetake={() => setMode('taking')} onClose={() => setMode('view')} />
      ) : (
        <div className={styles.viewLayout}>
          {/* Score summary */}
          <div className={styles.scoreSummary}>
            <div className={styles.currentScore} style={{ color: latestSev.color, background: latestSev.bg }}>
              <span className={styles.scoreNum}>{latestScore ?? '—'}</span>
              <span className={styles.scoreMax}>/ {scale.max}</span>
            </div>
            <div className={styles.scoreMeta}>
              <p className={styles.scoreSeverity} style={{ color: latestSev.color }}>{latestSev.label}</p>
              {latestScore != null && <TrendBadge current={latestScore} previous={previousScore} />}
              <p className={styles.scoreDate}>{latestDate ? `Last: ${new Date(latestDate).toLocaleDateString()}` : 'No completed administration'}</p>
            </div>
            <button className={styles.startBtn} onClick={() => setMode('taking')} disabled={!contextReady}>
              <ClipboardList size={14}/> Administer Now
            </button>
          </div>

          {/* Trend chart */}
          <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>{scale.abbr} Score Trend</h3>
              <span className={styles.chartSessions}>{history.length} verified administrations</span>
            </div>
            <ScoreChart history={history} scale={scale} />
          </div>

          {/* Severity guide */}
          <div className={styles.severityCard}>
            <h3 className={styles.guideTitle}>Severity Guide</h3>
            {scale.severity.map((s, i) => (
              <div key={i} className={styles.guideRow}>
                <div className={styles.guideDot} style={{ background: s.color }} />
                <span className={styles.guideLabel}>{s.label}</span>
                <span className={styles.guideRange} style={{ color: s.color }}>
                  {i === 0 ? `0–${s.max}` : `${scale.severity[i-1].max + 1}–${s.max}`}
                </span>
              </div>
            ))}
            <div className={styles.hipaaNote}>
              <Shield size={12}/> Results are PHI — stored securely per HIPAA
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
